import { Badge } from '@wedding-tech/ui/badge'
import { Button } from '@wedding-tech/ui/button'
import { ArrowLeft, Calendar, UserCheck } from 'lucide-react'
import type React from 'react'

import { useSettings } from '@/hooks/useSettings'
import { formatThaiDate } from '@/lib/date'

interface CheckinLayoutProps {
  title?: string
  subtitle?: React.ReactNode
  icon?: React.ReactNode
  onBack?: () => void
  children: React.ReactNode
}

export function CheckinLayout({
  title = 'เช็กอินเข้าร่วมงาน',
  subtitle,
  icon,
  onBack,
  children,
}: CheckinLayoutProps) {
  const { settings } = useSettings()

  const settingsData = (settings as any)?.data ?? settings ?? {}
  const eventTitle =
    settingsData.coupleNames ||
    settingsData.title ||
    settingsData.weddingTitle ||
    settingsData.name ||
    ''

  const rawDate =
    settingsData.eventDate ||
    settingsData.weddingDate ||
    settingsData.date ||
    settingsData.dateTime ||
    settingsData.rsvpDateRange?.start

  const eventDateDisplay = rawDate ? formatThaiDate(rawDate) : ''

  return (
    <div className="bg-background relative mx-auto flex min-h-dvh w-full max-w-md flex-col justify-between p-6">
      <div className="space-y-6 pt-4 pb-8">
        {/* Header Section */}
        <div className="relative space-y-3 text-center">
          {onBack && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="text-muted-foreground hover:text-foreground absolute top-0 left-0 size-9 rounded-full"
              aria-label="ย้อนกลับ"
            >
              <ArrowLeft className="size-5" />
            </Button>
          )}

          <div className="bg-primary/10 text-primary mx-auto flex size-14 items-center justify-center rounded-full">
            {icon || <UserCheck className="size-7" />}
          </div>

          <div className="space-y-1.5">
            {eventTitle && (
              <Badge
                variant="outline"
                className="text-primary border-primary/30 px-3 py-1 text-xs font-semibold"
              >
                {eventTitle}
              </Badge>
            )}
            <h1 className="text-foreground text-2xl font-bold tracking-tight">
              {title}
            </h1>
            {eventDateDisplay && (
              <p className="text-muted-foreground flex items-center justify-center gap-1.5 pt-0.5 text-xs">
                <Calendar className="text-primary size-3.5" />
                <span>{eventDateDisplay}</span>
              </p>
            )}
          </div>

          {subtitle && (
            <div className="text-muted-foreground pt-1 text-sm">{subtitle}</div>
          )}
        </div>

        {/* Content */}
        {children}
      </div>
    </div>
  )
}
